import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { DeleteConfirmationModal } from '@/shared/components/ui';
import {
  useStepsByFunnelId,
  useCreateFunnelStep,
  useUpdateFunnelStep,
  useDeleteFunnelStep
} from '../hooks';
import type { FunnelStepResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons'

/**
 * Gerenciador de etapas de um funil — formulário de criação/edição + lista
 * reordenável por drag & drop.
 *
 * Extraído de `FunnelsSettingsModal` (2026-07-17) para ser reusado tanto na
 * modal legada quanto no `FunnelsSettingsDrawer` (aba "Etapas").
 *
 * `onBack` é opcional: a modal legada usa (view empilhada → botão "Voltar
 * para Lista de Funis"); o drawer master-detail NÃO passa `onBack` — a lista
 * de funis já fica sempre visível à esquerda, então o botão não faz sentido lá.
 */
export interface StepsManagerProps {
  funnelId: string;
  onBack?: () => void;
}

export const StepsManager: React.FC<StepsManagerProps> = ({ funnelId, onBack }) => {
  const [stepForm, setStepForm] = useState({ name: '', description: '', color: '#8b5cf6' });
  const [editingStep, setEditingStep] = useState<FunnelStepResponse | null>(null);
  const [draggedStepId, setDraggedStepId] = useState<string | null>(null);
  const [stepToDelete, setStepToDelete] = useState<FunnelStepResponse | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: stepsData, isLoading } = useStepsByFunnelId(funnelId);
  const createStep = useCreateFunnelStep();
  const updateStep = useUpdateFunnelStep();
  const deleteStep = useDeleteFunnelStep();

  const steps = stepsData || [];

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, stepId: string) => {
    setDraggedStepId(stepId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', stepId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, targetStepId: string) => {
    e.preventDefault();

    if (!draggedStepId || draggedStepId === targetStepId) {
      setDraggedStepId(null);
      return;
    }

    const draggedIndex = steps.findIndex(s => s.id === draggedStepId);
    const targetIndex = steps.findIndex(s => s.id === targetStepId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedStepId(null);
      return;
    }

    // Reordenar localmente primeiro para feedback visual imediato
    const reorderedSteps = [...steps];
    const [draggedStep] = reorderedSteps.splice(draggedIndex, 1);
    reorderedSteps.splice(targetIndex, 0, draggedStep);

    // Atualizar ordem no backend
    try {
      await Promise.all(
        reorderedSteps.map((step, index) =>
          updateStep.mutateAsync({
            id: step.id,
            data: { order: index }
          })
        )
      );
    } catch (error) {
      console.error('Error reordering steps:', error);
    }

    setDraggedStepId(null);
  };

  const handleDragEnd = () => {
    setDraggedStepId(null);
  };

  const handleCreateStep = async () => {
    try {
      await createStep.mutateAsync({
        funnelId,
        name: stepForm.name,
        description: stepForm.description,
        color: stepForm.color,
        order: steps.length,
      });
      setStepForm({ name: '', description: '', color: '#8b5cf6' });
    } catch (error) {
      console.error('Error creating step:', error);
    }
  };

  const handleUpdateStep = async () => {
    if (!editingStep) return;

    try {
      await updateStep.mutateAsync({
        id: editingStep.id,
        data: {
          name: stepForm.name,
          description: stepForm.description,
          color: stepForm.color,
        },
      });
      setStepForm({ name: '', description: '', color: '#8b5cf6' });
      setEditingStep(null);
    } catch (error) {
      console.error('Error updating step:', error);
    }
  };

  const handleDeleteStep = (step: FunnelStepResponse) => {
    setStepToDelete(step);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!stepToDelete) return;

    try {
      await deleteStep.mutateAsync(stepToDelete.id);
      setIsDeleteModalOpen(false);
      setStepToDelete(null);
    } catch (error) {
      console.error('Error deleting step:', error);
    }
  };

  const handleStartEdit = (step: FunnelStepResponse) => {
    setEditingStep(step);
    setStepForm({
      name: step.name,
      description: step.description || '',
      color: step.color || '#8b5cf6',
    });
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
    setStepForm({ name: '', description: '', color: '#8b5cf6' });
  };

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="space-y-4">
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1e293b' }}
          >
            Nome da Etapa
          </label>
          <Input
            type="text"
            value={stepForm.name}
            onChange={(e) => setStepForm({ ...stepForm, name: e.target.value })}
            placeholder="Ex: Primeiro Contato, Qualificação, Proposta"
            data-testid="steps-manager-name"
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1e293b' }}
          >
            Descrição (Opcional)
          </label>
          <Textarea
            value={stepForm.description}
            onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
            rows={2}
            resize="none"
            placeholder="Descreva esta etapa do funil"
            data-testid="steps-manager-description"
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1e293b' }}
          >
            Cor da Etapa
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={stepForm.color}
              onChange={(e) => setStepForm({ ...stepForm, color: e.target.value })}
              className="w-12 h-12 rounded cursor-pointer border"
              style={{ borderColor: '#d1d5db' }}
              data-testid="steps-manager-color-picker"
            />
            <div className="flex-1">
              <Input
                type="text"
                value={stepForm.color}
                onChange={(e) => setStepForm({ ...stepForm, color: e.target.value })}
                className="font-mono text-sm"
                placeholder="#8b5cf6"
                data-testid="steps-manager-color-hex"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={editingStep ? handleUpdateStep : handleCreateStep}
            variant="primary"
            disabled={!stepForm.name.trim()}
            className="flex-1"
            data-testid="steps-manager-submit"
          >
            {editingStep ? 'Salvar Alterações' : 'Adicionar Etapa'}
          </Button>
          {editingStep && (
            <Button onClick={handleCancelEdit} variant="secondary" data-testid="steps-manager-cancel-edit">
              Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* Steps List */}
      <div>
        <h3
          className="text-sm font-medium mb-3"
          style={{ color: '#1e293b' }}
        >
          Etapas Cadastradas ({steps.length})
        </h3>

        {isLoading ? (
          <div className="text-center py-4" style={{ color: '#64748b' }}>
            Carregando etapas...
          </div>
        ) : steps.length === 0 ? (
          <div className="text-center py-4" style={{ color: '#64748b' }}>
            Nenhuma etapa cadastrada ainda.
          </div>
        ) : (
          <div className="space-y-2">
            {steps.map((step, _index) => (
              <div
                key={step.id}
                draggable
                onDragStart={(e) => handleDragStart(e, step.id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, step.id)}
                onDragEnd={handleDragEnd}
                className="flex items-center gap-3 p-3 rounded border transition-colors"
                style={{
                  borderColor: draggedStepId === step.id ? '#8b5cf6' : '#e5e7eb',
                  backgroundColor: draggedStepId === step.id ? '#f3e8ff' : (editingStep?.id === step.id ? '#f1f5f9' : 'transparent'),
                  opacity: draggedStepId === step.id ? 0.5 : 1,
                  cursor: 'grab'
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.cursor = 'grabbing';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.cursor = 'grab';
                }}
                data-testid={`steps-manager-row-${step.id}`}
              >
                <Icon name="drag" size={16}
                  style={{ color: '#64748b', cursor: 'grab' }} />
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: step.color }}
                />
                <div className="flex-1">
                  <div
                    className="font-medium"
                    style={{ color: '#1e293b' }}
                  >
                    {step.name}
                  </div>
                  {step.description && (
                    <div
                      className="text-sm"
                      style={{ color: '#64748b' }}
                    >
                      {step.description}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartEdit(step);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="p-1.5 rounded transition-colors"
                    style={{ color: '#1e293b', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Editar etapa"
                    data-testid={`steps-manager-edit-${step.id}`}
                  >
                    <Icon name="edit" size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteStep(step);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="p-1.5 rounded transition-colors"
                    style={{ color: '#ef4444', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Excluir etapa"
                    data-testid={`steps-manager-delete-${step.id}`}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Back Button — só exibido quando o consumidor fornece onBack (modal legada) */}
      {onBack && (
        <Button onClick={onBack} variant="secondary" className="w-full" data-testid="steps-manager-back">
          Voltar para Lista de Funis
        </Button>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setStepToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Excluir Etapa"
        message={`Tem certeza que deseja excluir a etapa "${stepToDelete?.name}"?`}
        warningMessage="Excluir esta etapa também irá excluir todos os leads dentro dela. Esta ação não pode ser desfeita."
        isLoading={deleteStep.isPending}
        confirmButtonText="Excluir Etapa"
      />
    </div>
  );
};

StepsManager.displayName = 'StepsManager';
